import { offset as offsetModifier, Options } from '@popperjs/core';

/**
 * Default offset value for ng-select dropdown positioning using Popper.js.
 *
 * @remarks
 * This value represents the offset in pixels `[skidding, distance]` used by Popper.js
 * to position dropdown menus relative to their trigger elements.
 * The value must be kept in sync with the `$ng-select-dropdown-offset` SCSS variable.
 *
 * @see This constant must match the SCSS variable in the style configuration.
 */
// This value is equal to ng-select dropdown offset defined by `$ng-select-dropdown-offset` variable in SCSS.
// Changing it here will require changing it in SCSS as well.
export const DEFAULT_OFFSET = [0, 8];

/**
 * Creates a Popper.js modifier function that applies Backbase-specific dropdown offset positioning.
 *
 * @param offset - The offset values `[skidding, distance]` to apply. Defaults to {@link DEFAULT_OFFSET}
 * @returns A function that accepts and configures {@link Options} with the offset modifier
 *
 * @remarks
 * This utility integrates with Popper.js to customize dropdown positioning for ng-select components.
 * It adds the offset modifier to Popper.js options, enabling consistent dropdown spacing
 * across Backbase UI components.
 *
 * @example
 *
 * ```typescript
 * const popperConfig: Partial<Options> = {
 *   modifiers: []
 * };
 * const configureOffset = addBBPopperOffset([0, 12]);
 * const finalConfig = configureOffset(popperConfig);
 * ```
 */
export function addBBPopperOffset(offset: number[] = DEFAULT_OFFSET) {
  return (options: Partial<Options>) => {
    options.modifiers!.push(offsetModifier, {
      name: 'offset',
      options: {
        offset: () => offset,
      },
    });

    return options;
  };
}
